import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import {
	RUNDOWN_TEMPLATE,
	type RundownTemplateDetailResponseType,
	type RundownTemplateOverviewResponseType,
	type RundownTemplateStateType,
} from "./types";

const rundownTemplateInitialState: RundownTemplateStateType = {
	create: {
		data: null,
		submitting: false,
		success: null,
		error: null,
	},
	edit: {
		data: null,
		submitting: false,
		success: null,
		error: null,
	},
	detail: {
		data: null,
		deleting: false,
		loading: true,
		error: null,
	},
	overview: {
		data: null,
		loading: true, // start with loading state to avoid flash
		error: null,
	},
};

export const rundownSlice = createSlice({
	name: RUNDOWN_TEMPLATE,
	initialState: rundownTemplateInitialState,
	reducers: {
		requestCreateRundownTemplate: (rundown: RundownTemplateStateType) => {
			rundown.create.submitting = true;
			rundown.create.success = null;
			rundown.create.error = null;
		},
		receiveCreateRundownTemplate: (
			rundown: RundownTemplateStateType,
			{ payload }: PayloadAction<RundownTemplate>,
		) => {
			rundown.create.submitting = false;
			rundown.create.success = { completed: true, rundownTemplateId: payload.id };
			rundown.create.error = null;
			rundown.create.data = payload;
		},
		failedCreateRundownTemplate: (
			rundown: RundownTemplateStateType,
			{ payload: error }: PayloadAction<string>,
		) => {
			rundown.create.submitting = false;
			rundown.create.success = null;
			rundown.create.error = error;
		},
		requestDeleteRundownTemplate: (rundown: RundownTemplateStateType) => {
			rundown.detail.deleting = true;
		},
		receiveDeleteRundownTemplate: (
			rundown: RundownTemplateStateType,
			{ payload }: PayloadAction<string>,
		) => {
			if (rundown.overview.data) {
				rundown.overview.data = rundown.overview.data.filter(
					(rundownTemplate) => rundownTemplate.id !== payload,
				);
			}
			rundown.detail.deleting = false;
		},
		failedDeleteRundownTemplate: (rundown: RundownTemplateStateType) => {
			rundown.detail.deleting = false;
		},
		requestUpdateRundownTemplate: (rundown: RundownTemplateStateType) => {
			rundown.edit.submitting = true;
			rundown.edit.success = null;
			rundown.edit.error = null;
		},
		receiveUpdateRundownTemplate: (
			rundown: RundownTemplateStateType,
			{ payload }: PayloadAction<RundownTemplate>,
		) => {
			rundown.edit.submitting = false;
			rundown.edit.success = { completed: true, rundownTemplateId: payload.id };
			rundown.edit.error = null;
		},
		failedUpdateRundownTemplate: (
			rundown: RundownTemplateStateType,
			{ payload: error }: PayloadAction<string>,
		) => {
			rundown.edit.submitting = false;
			rundown.edit.success = null;
			rundown.edit.error = error;
		},
		requestGetRundownTemplates: (rundown: RundownTemplateStateType) => {
			rundown.overview.loading = true;
		},
		receiveGetRundownTemplates: (
			rundown: RundownTemplateStateType,
			{ payload }: PayloadAction<RundownTemplateOverviewResponseType>,
		) => {
			rundown.overview.loading = false;
			rundown.overview.data = payload;
		},
		failedGetRundownTemplates: (
			rundown: RundownTemplateStateType,
			{ payload: error }: PayloadAction<string>,
		) => {
			rundown.overview.loading = false;
			rundown.overview.error = error;
		},
		requestGetRundownTemplate: (rundown: RundownTemplateStateType) => {
			rundown.detail.loading = true;
		},
		receiveGetRundownTemplate: (
			rundown: RundownTemplateStateType,
			{ payload }: PayloadAction<RundownTemplateDetailResponseType | null>,
		) => {
			rundown.detail.loading = false;
			rundown.detail.data = payload;
			if (rundown.detail.data && payload) {
				rundown.detail.data.graphicPackageId = payload.graphicPackage?.id || "";
			}
		},
		failedGetRundownTemplate: (
			rundown: RundownTemplateStateType,
			{ payload: error }: PayloadAction<string>,
		) => {
			rundown.detail.loading = false;
			rundown.detail.error = error;
		},
		clearRundownTemplateSuccess: (rundown: RundownTemplateStateType) => {
			rundown.create.success = null;
			rundown.edit.success = null;
		},
	},
});

const actions = rundownSlice.actions;
const rundownTemplateReducer = rundownSlice.reducer;

export { actions, rundownTemplateReducer };
